import React from 'react'
import {
  AlertIcon,
  CheckDoubleIcon,
  CheckIcon,
  ClockIcon,
} from './icons'
import type { Delivery, Operator, ViewMessage, WebChatLabels } from './types'

type ChatMessageProps = {
  message: ViewMessage
  operator: Operator
  labels: WebChatLabels
}

const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')

const Receipt: React.FC<{ delivery: Delivery; labels: WebChatLabels }> = ({
  delivery,
  labels,
}) => {
  const map: Record<Delivery, { icon: React.ReactNode; text: string }> = {
    sending: { icon: <ClockIcon />, text: labels.receiptSending },
    sent: { icon: <CheckIcon />, text: labels.receiptSent },
    read: { icon: <CheckDoubleIcon />, text: labels.receiptRead },
    failed: { icon: <AlertIcon />, text: labels.receiptFailed },
  }
  const { icon, text } = map[delivery]
  return (
    <span className="wc-receipt">
      {icon}
      <span className="wc-sr">{text}</span>
    </span>
  )
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  operator,
  labels,
}) => {
  const { kind, text, timestamp, delivery, dayLabel, showAvatar } = message

  const day = dayLabel ? (
    <div className="wc-day">
      <span>{dayLabel}</span>
    </div>
  ) : null

  if (kind === 'system') {
    return (
      <>
        {day}
        <div className="wc-system">{text}</div>
      </>
    )
  }

  const out = kind === 'outbound'
  const senderPrefix = out ? labels.senderYou : labels.senderOperator

  return (
    <>
      {day}
      <div className={`wc-row ${out ? 'wc-row--out' : ''}`}>
        {!out &&
          (showAvatar ? (
            <span className="wc-avatar" aria-hidden="true">
              {operator.avatarUrl ? (
                <img src={operator.avatarUrl} alt="" />
              ) : (
                initials(operator.name)
              )}
            </span>
          ) : (
            // keep the bubble aligned with the avatar column
            <span className="wc-avatar" style={{ visibility: 'hidden' }} />
          ))}
        <div className="wc-col">
          <div
            className={`wc-bubble ${out ? 'wc-bubble--out' : 'wc-bubble--in'} ${
              delivery === 'failed' ? 'wc-bubble--failed' : ''
            }`}
          >
            <span className="wc-sr">{senderPrefix} </span>
            {text}
          </div>
          {(timestamp || (out && delivery)) && (
            <div className="wc-meta">
              {timestamp && <span>{timestamp}</span>}
              {out && delivery && (
                <Receipt delivery={delivery} labels={labels} />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
